import Script from "next/script";
import { type Article, type AuthorProfile } from "@/lib/types";
import { getCountryInfo } from "@/lib/countries";
import { getCanonicalAuthorName } from "@/lib/authors";

const DOMAIN = "https://newsafrika.com";

const ORG_ID = `${DOMAIN}/#organization`;
const WEBSITE_ID = `${DOMAIN}/#website`;
const KG_ID = "https://www.google.com/search?kgmid=/g/11z0lfhwd5";
const WIKIDATA_ID = "https://www.wikidata.org/wiki/Q138662029";

function ensureAbsolute(url: string): string {
  if (!url) return DOMAIN;
  if (url.startsWith('http')) return url;
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${DOMAIN}${cleanPath}`;
}

const ORGANIZATION_NODE = {
  "@type": "NewsMediaOrganization",
  "@id": ORG_ID,
  name: "News Afrika",
  alternateName: "NWA",
  url: DOMAIN,
  logo: {
    "@type": "ImageObject",
    "@id": `${DOMAIN}/#logo`,
    url: `${DOMAIN}/logo.svg`,
    width: 900,
    height: 300
  },
  description:
    "News Afrika is an independent digital newsroom covering politics, security, economy, sports and investigations across Africa and globally.",
  foundingDate: "2024-01-01",
  publishingPrinciples: `${DOMAIN}/editorial-policy`,
  ethicsPolicy: `${DOMAIN}/ethics-policy`,
  correctionsPolicy: `${DOMAIN}/corrections-policy`,
  unnamedSourcesPolicy: `${DOMAIN}/editorial-policy`,
  sameAs: [
    "https://www.facebook.com/newsafrika24",
    "https://x.com/newsafrika24",
    "https://www.instagram.com/newsafrika24",
    "https://www.tiktok.com/@newsafrika24",
    "https://flipboard.com/@newsafrika24",
    KG_ID,
    WIKIDATA_ID
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "editorial",
    email: "editorial@newsafrika.com",
    availableLanguage: ["en"]
  },
  areaServed: {
    "@type": "Place",
    name: "Africa"
  }
};

const WEBSITE_NODE = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: DOMAIN,
  name: "News Afrika",
  publisher: { "@id": ORG_ID },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${DOMAIN}/search?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

export function GlobalSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [ORGANIZATION_NODE, WEBSITE_NODE]
  };

  return (
    <Script
      id="global-schema"
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function ArticleSchema({
  article,
  url,
  breadcrumbItems,
  authorProfile
}: {
  article: Article;
  url: string;
  breadcrumbItems: { name: string; url: string }[];
  authorProfile?: AuthorProfile | null;
}) {
  const countryInfo = getCountryInfo(article.country || "za");

  const textContent =
    article.body
      ?.map((block) => block.text || block.items?.join(" ") || "")
      .join(" ") || "";

  const wordCount = textContent.split(/\s+/).filter(Boolean).length;

  const authorSlug = article.authorProfileSlug || article.authorId || 'newsroom';
  const authorId = `${DOMAIN}/authors/${authorSlug}#person`;
  const canonicalName = getCanonicalAuthorName(article, authorProfile);

  const publishDate = article.publishDate || new Date().toISOString();
  const modifiedDate = article.updatedAt || publishDate;
  const primaryImage = article.images?.[0]?.src || `${DOMAIN}/logo.svg`;

  let videoNode = null;
  if (article.video && (article.video.contentUrl || article.video.embedUrl)) {
    videoNode = {
      "@type": "VideoObject",
      "name": article.video.title || article.headline,
      "description": article.video.description || article.subheadline || article.lede,
      "thumbnailUrl": article.video.thumbnail || primaryImage,
      "uploadDate": publishDate,
      "contentUrl": article.video.contentUrl,
      "embedUrl": article.video.embedUrl,
      "potentialAction": {
        "@type": "WatchAction",
        "target": [ `${DOMAIN}/video/${article.slug}` ]
      }
    };
  }

  const graph: any[] = [
    ORGANIZATION_NODE,
    WEBSITE_NODE,
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: article.headline,
      isPartOf: { "@id": WEBSITE_ID },
      breadcrumb: { "@id": `${url}#breadcrumb` },
      mainEntity: { "@id": `${url}#article` }
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: breadcrumbItems.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        item: ensureAbsolute(item.url)
      }))
    },
    {
      "@type": "NewsArticle",
      "@id": `${url}#article`,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url
      },
      headline: article.headline,
      description: article.seoDescription || article.subheadline || article.lede,
      articleBody: textContent.slice(0, 15000),
      genre: "News",
      articleSection: [article.primaryCategory],
      keywords: article.tags?.join(", "),
      inLanguage: countryInfo.lang,
      datePublished: publishDate,
      dateModified: modifiedDate,
      wordCount,
      isAccessibleForFree: true,
      speakable: {
        "@type": "SpeakableSpecification",
        "cssSelector": [".article-headline", ".article-lede"]
      },
      author: {
        "@id": authorId
      },
      publisher: { "@id": ORG_ID },
      image: article.images?.length
        ? article.images.map((img, i) => ({
            "@type": "ImageObject",
            "@id": `${url}#primaryimage-${i}`,
            url: img.src,
            width: 1200,
            height: 675,
            representativeOfPage: i === 0
          }))
        : [
            {
              "@type": "ImageObject",
              url: primaryImage,
              width: 1200,
              height: 675,
              representativeOfPage: true
            }
          ],
      thumbnailUrl: primaryImage,
      dateline: countryInfo.name,
      contentLocation: {
        "@type": "Country",
        name: countryInfo.name
      },
      copyrightYear: new Date(publishDate).getFullYear(),
      copyrightHolder: { "@id": ORG_ID },
      video: videoNode
    },
    {
      "@type": "Person",
      "@id": authorId,
      "name": canonicalName,
      "url": `${DOMAIN}/authors/${authorSlug}`,
      "image": authorProfile?.images?.[0]?.src || authorProfile?.image || primaryImage,
      "description": authorProfile?.bio,
      "jobTitle": "Journalist",
      "worksFor": { "@id": ORG_ID },
      "sameAs": authorProfile?.socialLinks || []
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@graph": graph
  };

  return (
    <Script
      id={`article-schema-${article.id}`}
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function VideoPageSchema({
  article,
  url
}: {
  article: Article;
  url: string;
}) {
  const primaryImage = article.images?.[0]?.src || `${DOMAIN}/logo.svg`;
  
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      ORGANIZATION_NODE,
      {
        "@type": "VideoObject",
        "@id": `${url}#video`,
        "name": article.video?.title || article.headline,
        "description": article.video?.description || article.subheadline || article.lede,
        "thumbnailUrl": article.video?.thumbnail || primaryImage,
        "uploadDate": article.publishDate,
        "contentUrl": article.video?.contentUrl,
        "embedUrl": article.video?.embedUrl,
        "duration": article.video?.duration,
        "interactionStatistic": {
          "@type": "InteractionCounter",
          "interactionType": { "@type": "WatchAction" },
          "userInteractionCount": 1
        },
        "publisher": { "@id": ORG_ID },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": url
        }
      }
    ]
  };

  return (
    <Script
      id={`video-schema-${article.id}`}
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function BreadcrumbSchema({
  items
}: {
  items: { name: string; url: string }[];
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: ensureAbsolute(item.url)
    }))
  };

  return (
    <Script
      id="breadcrumb-schema"
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function CategorySchema({
  name,
  url,
  articles
}: {
  name: string;
  url: string;
  articles: Article[];
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#collection`,
    "url": url,
    "name": name,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": articles.slice(0, 15).map((a, i) => {
        const countryCode = (a.country || "za").toLowerCase();
        const cat = (a.primaryCategory || "news").toLowerCase().replace(/\s+/g, "-");
        return {
          "@type": "ListItem",
          "position": i + 1,
          "url": `${DOMAIN}/${cat}/${countryCode}/${a.slug}`
        };
      })
    },
    "publisher": { "@id": ORG_ID }
  };

  return (
    <Script
      id={`category-schema-${name.toLowerCase().replace(/\s+/g, "-")}`}
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function TopicSchema({
  name,
  url,
  description
}: {
  name: string;
  url: string;
  description: string;
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${url}#webpage`,
    "url": url,
    "name": name,
    "description": description,
    "publisher": { "@id": ORG_ID },
    "about": {
      "@type": "Thing",
      "name": name
    }
  };

  return (
    <Script
      id={`topic-schema-${name.toLowerCase().replace(/\s+/g, "-")}`}
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function AuthorSchema({ author }: { author: AuthorProfile }) {
  const authorUrl = `${DOMAIN}/authors/${author.profileSlug}`;
  const personId = `${authorUrl}#person`;
  
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ProfilePage",
        "@id": `${authorUrl}#profilepage`,
        "url": authorUrl,
        "name": `${author.name} Profile | News Afrika`,
        "mainEntity": { "@id": personId }
      },
      {
        "@type": "Person",
        "@id": personId,
        "name": author.name,
        "url": authorUrl,
        "image": author.images?.[0]?.src || author.image,
        "description": author.bio,
        "jobTitle": "Journalist",
        "worksFor": { "@id": ORG_ID },
        "knowsAbout": author.areasOfExpertise || [],
        "sameAs": author.socialLinks || []
      }
    ]
  };

  return (
    <Script
      id={`author-schema-${author.profileSlug}`}
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function FAQSchema({
  faqs
}: {
  faqs: { question: string; answer: string }[];
}) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <Script
      id="faq-schema"
      type="application/ld+json"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
